import type { Resource, ResourceTypeHandle, Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { applyResourceSelection } from '@toolproof-core/visualization/derivation/_pure/apply-resource-selection';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { SpaceRenderOptions } from '@toolproof-core/visualization/_contract/space-render-options';
import type {
    ConstellationDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { RESOURCE_TYPE_REQUIREMENTS } from '@/workflow/_data/config/fragments/resourceTypes';
import { TYPE_ResourceType, TYPE_Tool } from '@/workflow/_data/cosmosData';
import { getResourceTypesUsedByTools } from '@/workflow/_pure/cosmosData';
import { getProjectionHandle } from '@/workflow/_pure/projectionAccess';
import {
    deriveRoleItemCollectionDerivations,
    deriveToolRoleConnectorItemCollectionDerivations,
    toResourceCollection,
} from '@/workflow/_pure/derivation/collections';
import {
    deriveConstellationDerivations,
    enrichConstellationDerivations,
} from '@/workflow/_pure/derivation/constellations';
import type { BaseConfig } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';

function filterResourcesByHandle<THandle extends string>(
    resources: Resource[],
    selectedHandles: ReadonlySet<THandle>
): Resource[] {
    return applyResourceSelection(resources, {
        filter: (resource) => {
            const handle = getProjectionHandle<THandle>(resource);
            return handle !== null && selectedHandles.has(handle);
        },
    });
}

function resolveToolHandles(
    config: BaseConfig,
    cosmosData: CosmosDataModel
): Set<ToolHandle> {
    const configuredToolHandles = config.toolSelection?.toolHandles ?? [];
    if (configuredToolHandles.length > 0) {
        return new Set<ToolHandle>(configuredToolHandles);
    }

    return new Set<ToolHandle>(cosmosData.toolMap.keys());
}

export function deriveResourceCollections(input: {
    cosmosData: CosmosDataModel;
    config: BaseConfig;
}) {
    const { cosmosData, config } = input;
    const resourcesByType = cosmosData.resourcesByType;
    const toolHandles = resolveToolHandles(config, cosmosData);
    const toolMap = new Map<ToolHandle, Tool>(
        Array.from(cosmosData.toolMap.entries()).filter(([handle]) => toolHandles.has(handle))
    );
    const resourceTypeHandles = new Set<ResourceTypeHandle>(
        getResourceTypesUsedByTools(toolMap)
    );

    const toolResources = filterResourcesByHandle<ToolHandle>(
        resourcesByType[TYPE_Tool] ?? [],
        toolHandles
    );

    const resourceTypeResources = filterResourcesByHandle<ResourceTypeHandle>(
        resourcesByType[TYPE_ResourceType] ?? [],
        resourceTypeHandles
    );

    return RESOURCE_TYPE_REQUIREMENTS.map((resourceTypeHandle) => {
        if (resourceTypeHandle === TYPE_Tool) {
            return toResourceCollection(resourceTypeHandle, toolResources);
        }

        if (resourceTypeHandle === TYPE_ResourceType) {
            return toResourceCollection(resourceTypeHandle, resourceTypeResources);
        }

        return toResourceCollection(resourceTypeHandle, []);
    });
}

export function deriveSyntheticItemCollectionDerivations(input: {
    cosmosData: CosmosDataModel;
    config: BaseConfig;
}) {
    const { cosmosData, config } = input;
    const toolHandles = resolveToolHandles(config, cosmosData);
    const toolMap = new Map<ToolHandle, Tool>(
        Array.from(cosmosData.toolMap.entries()).filter(([handle]) => toolHandles.has(handle))
    );
    const resourceTypeHandles = new Set<ResourceTypeHandle>(
        getResourceTypesUsedByTools(toolMap)
    );

    const toolResources = filterResourcesByHandle<ToolHandle>(
        cosmosData.resourcesByType[TYPE_Tool] ?? [],
        toolHandles
    );

    const roleItemCollectionDerivations = deriveRoleItemCollectionDerivations({
        toolResources,
        resourceTypeHandles,
        config,
    });

    const toolRoleConnectorItemCollectionDerivations = deriveToolRoleConnectorItemCollectionDerivations({
        toolResources,
        resourceTypeHandles,
        config,
    });

    return [...roleItemCollectionDerivations, ...toolRoleConnectorItemCollectionDerivations];
}

export function deriveBaseConstellationDerivations(args: {
    itemCollections: ItemCollection[];
    config: BaseConfig;
}): ConstellationDerivation[] {
    return deriveConstellationDerivations({
        itemCollections: args.itemCollections,
        config: args.config,
        toolResourceTypeHandle: TYPE_Tool,
        resourceTypeResourceTypeHandle: TYPE_ResourceType,
    });
}

export function deriveBaseSpaceRenderOptions(args: {
    config: BaseConfig;
}): SpaceRenderOptions {
    return {
        backgroundColor: args.config.spaceRenderOptions?.backgroundColor,
        camera: args.config.spaceRenderOptions?.camera,
    };
}

export function enrichBaseConstellationDerivations(args: {
    constellationDerivations: readonly ConstellationDerivation[];
    config: BaseConfig;
}): ConstellationDerivation[] {
    return enrichConstellationDerivations({
        constellationDerivations: args.constellationDerivations,
        config: args.config,
    });
}
